import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Checkbox } from './ui/checkbox';
import { toast } from 'sonner';
import { loadAPI } from '../api';
import { 
  FileText, Download, RefreshCw, Plus, Trash2, 
  ArrowUp, ArrowDown, History
} from 'lucide-react';

// Default values for preloaded fields
const DEFAULT_THIRD_PARTY = {
  name: 'Freight Tec Management Group',
  address: '190 N Main Street Suite 225',
  city_state_zip: 'Bountiful, Utah 84010'
};
const DEFAULT_SCAC = 'FTMG';
const DEFAULT_CARRIER_NAME = 'Freight Tec';

const BOLGenerator = ({ 
  loadId, 
  load, 
  facilities, 
  carriers 
}) => {
  // BOL state with preloaded defaults
  const [bolData, setBolData] = useState({
    bol_number: '',
    pro_number: '',
    trailer_number: '',
    seal_number: '',
    scac: DEFAULT_SCAC,
    carrier_name: DEFAULT_CARRIER_NAME,
    // Third party bill to - preloaded with defaults
    third_party_name: DEFAULT_THIRD_PARTY.name,
    third_party_address: DEFAULT_THIRD_PARTY.address,
    third_party_city_state_zip: DEFAULT_THIRD_PARTY.city_state_zip,
    // Freight charge terms
    freight_terms: 'third_party', // 'prepaid', 'collect', 'third_party'
    master_bol: false,
    // Special instructions (single field)
    special_instructions: '',
    // Line items for the table
    line_items: [
      { item_description: '', num_packages: '', weight: '', pallet_count: '' }
    ],
    weight_unit: 'LBS', // 'LBS' or 'KGS'
    additional_shipper_info: '',
    // Calculated totals
    total_packages: 0,
    total_weight: 0,
    total_pallets: 0,
  });
  const [bolVersions, setBolVersions] = useState([]);
  const [generatingPdf, setGeneratingPdf] = useState(false);
  const [saving, setSaving] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  // Load BOL data when load changes - auto-fill trailer number from load
  useEffect(() => {
    if (load) {
      // Always use load.trailer_number as the source of truth for Trailer #
      const effectiveTrailer = load.trailer_number || '';
      
      if (load.bol_data) {
        // Load saved BOL data, but always use load.trailer_number for Trailer #
        setBolData(prev => ({
          ...prev,
          bol_number: load.bol_data.bol_number || '',
          pro_number: load.bol_data.pro_number || '',
          trailer_number: effectiveTrailer, // Always from load's Carrier tab
          seal_number: load.bol_data.seal_number || '',
          scac: load.bol_data.scac || DEFAULT_SCAC,
          carrier_name: load.bol_data.carrier_name || DEFAULT_CARRIER_NAME,
          third_party_name: load.bol_data.third_party_name || DEFAULT_THIRD_PARTY.name,
          third_party_address: load.bol_data.third_party_address || DEFAULT_THIRD_PARTY.address,
          third_party_city_state_zip: load.bol_data.third_party_city_state_zip || DEFAULT_THIRD_PARTY.city_state_zip,
          freight_terms: load.bol_data.freight_terms || 'third_party',
          master_bol: load.bol_data.master_bol || false,
          special_instructions: load.bol_data.special_instructions || '',
          line_items: load.bol_data.line_items || [{ item_description: '', num_packages: '', weight: '', pallet_count: '' }],
          weight_unit: load.bol_data.weight_unit || 'LBS',
          additional_shipper_info: load.bol_data.additional_shipper_info || '',
        }));
        if (load.bol_versions) {
          setBolVersions(load.bol_versions);
        }
      } else {
        // No saved BOL data - auto-fill trailer number from load's Carrier tab
        setBolData(prev => ({
          ...prev,
          trailer_number: effectiveTrailer,
        }));
      }
      setHasUnsavedChanges(false);
    }
  }, [load]);

  // Sync trailer number from Carrier tab in real-time
  useEffect(() => {
    if (load?.trailer_number !== undefined) {
      setBolData(prev => ({
        ...prev,
        trailer_number: load.trailer_number || ''
      }));
    }
  }, [load?.trailer_number]);

  // Auto-generate BOL from load data
  const autoGenerateBol = () => {
    setBolData(prev => ({
      ...prev,
      bol_number: load?.load_number || loadId?.substring(0, 8).toUpperCase() || '',
      pro_number: load?.load_number || loadId?.substring(0, 8).toUpperCase() || '',
      trailer_number: load?.trailer_number || prev.trailer_number || '',
      // Keep defaults for carrier info
      scac: prev.scac || DEFAULT_SCAC,
      carrier_name: prev.carrier_name || DEFAULT_CARRIER_NAME,
      third_party_name: prev.third_party_name || DEFAULT_THIRD_PARTY.name,
      third_party_address: prev.third_party_address || DEFAULT_THIRD_PARTY.address,
      third_party_city_state_zip: prev.third_party_city_state_zip || DEFAULT_THIRD_PARTY.city_state_zip,
    }));
    setHasUnsavedChanges(true);
    toast.success('BOL auto-generated from load data');
  };

  // Helper to update BOL data and mark as unsaved
  const updateBolField = (field, value) => {
    setBolData(prev => ({ ...prev, [field]: value }));
    setHasUnsavedChanges(true);
  };

  // Line item management
  const addLineItem = () => {
    setBolData(prev => ({
      ...prev,
      line_items: [
        ...prev.line_items,
        { item_description: '', num_packages: '', weight: '', pallet_count: '' }
      ]
    }));
    setHasUnsavedChanges(true);
  };

  const removeLineItem = (index) => {
    if (bolData.line_items.length > 1) {
      setBolData(prev => ({
        ...prev,
        line_items: prev.line_items.filter((_, i) => i !== index)
      }));
      setHasUnsavedChanges(true);
    }
  };

  const updateLineItem = (index, field, value) => {
    setBolData(prev => ({
      ...prev,
      line_items: prev.line_items.map((item, i) => 
        i === index ? { ...item, [field]: value } : item
      )
    }));
    setHasUnsavedChanges(true);
  };

  // Calculate totals
  const calculateTotals = () => {
    return bolData.line_items.reduce((acc, item) => ({
      totalPackages: acc.totalPackages + (parseInt(item.num_packages) || 0),
      totalWeight: acc.totalWeight + (parseFloat(item.weight) || 0),
      totalPallets: acc.totalPallets + (parseInt(item.pallet_count) || 0),
    }), { totalPackages: 0, totalWeight: 0, totalPallets: 0 });
  };

  // Save BOL data
  const saveBolData = async () => {
    setSaving(true);
    try {
      const totals = calculateTotals();
      const bolPayload = {
        ...bolData,
        ...totals,
        updated_at: new Date().toISOString()
      };
      
      await loadAPI.update(loadId, { bol_data: bolPayload });
      
      // Add to version history (keep last 10)
      setBolVersions(prev => [{
        ...bolPayload,
        created_at: new Date().toISOString()
      }, ...prev].slice(0, 10));
      
      setHasUnsavedChanges(false);
      toast.success('BOL data saved successfully');
    } catch (error) {
      console.error('Save error:', error);
      toast.error('Failed to save BOL data');
    } finally {
      setSaving(false);
    }
  };

  // Restore a previous version
  const restoreVersion = (version) => {
    setBolData({
      bol_number: version.bol_number || '',
      pro_number: version.pro_number || '',
      trailer_number: version.trailer_number || load?.trailer_number || '',
      seal_number: version.seal_number || '',
      scac: version.scac || DEFAULT_SCAC,
      carrier_name: version.carrier_name || DEFAULT_CARRIER_NAME,
      third_party_name: version.third_party_name || DEFAULT_THIRD_PARTY.name,
      third_party_address: version.third_party_address || DEFAULT_THIRD_PARTY.address,
      third_party_city_state_zip: version.third_party_city_state_zip || DEFAULT_THIRD_PARTY.city_state_zip,
      freight_terms: version.freight_terms || 'third_party',
      master_bol: version.master_bol || false,
      special_instructions: version.special_instructions || '',
      line_items: version.line_items || [{ item_description: '', num_packages: '', weight: '', pallet_count: '' }],
      weight_unit: version.weight_unit || 'LBS',
      additional_shipper_info: version.additional_shipper_info || '',
    });
    setHasUnsavedChanges(true);
    toast.success('BOL version restored - click Save to keep changes');
  };

  // Export to PDF - Exact Greentree Transportation BOL Format on US Letter 8.5" x 11"
  const exportPdf = () => {
    setGeneratingPdf(true);
    try {
      const pickup = load?.stops?.find(s => s.type === 'pickup');
      const delivery = load?.stops?.filter(s => s.type === 'delivery')[0];
      const pickupFacility = facilities?.find(f => f.id === pickup?.facility_id);
      const deliveryFacility = facilities?.find(f => f.id === delivery?.facility_id);
      const totals = calculateTotals();
      // Use load's trailer_number as source of truth
      const pdfTrailerNumber = load?.trailer_number || bolData.trailer_number || '';

      // Build the PDF HTML matching the exact Greentree Transportation BOL template
      const bolHtml = `
        <!DOCTYPE html>
        <html>
        <head>
          <meta charset="UTF-8">
          <title>Bill of Lading - ${bolData.bol_number}</title>
          <style>
            @page {
              size: letter;
              margin: 0.3in;
            }
            * { box-sizing: border-box; margin: 0; padding: 0; }
            body { 
              font-family: Arial, Helvetica, sans-serif; 
              font-size: 9pt; 
              line-height: 1.25;
              width: 8in;
              margin: 0 auto;
              padding: 0.1in;
              background: white;
            }
            
            /* Main container */
            .bol-container {
              border: 2px solid #000;
              width: 100%;
            }
            
            /* Header */
            .bol-header {
              display: flex;
              justify-content: space-between;
              align-items: flex-start;
              padding: 6px 10px;
              border-bottom: 2px solid #000;
            }
            .bol-header h1 {
              font-size: 16pt;
              font-weight: bold;
              letter-spacing: 1px;
            }
            .header-right {
              text-align: right;
            }
            .page-number {
              font-size: 8pt;
              margin-bottom: 4px;
            }
            .bol-number-row {
              font-size: 9pt;
            }
            .bol-number-value {
              font-weight: bold;
              font-size: 10pt;
            }
            
            /* Barcode space */
            .barcode-space {
              border: 1px dashed #888;
              height: 30px;
              margin: 4px 0;
              display: flex;
              align-items: center;
              justify-content: center;
              color: #888;
              font-size: 7pt;
              background: #fafafa;
            }
            
            /* Main content grid - 2 columns */
            .main-row {
              display: flex;
              border-bottom: 1px solid #000;
            }
            .col-left {
              width: 50%;
              border-right: 1px solid #000;
            }
            .col-right {
              width: 50%;
            }
            
            /* Section box */
            .section-box {
              padding: 6px 8px;
              min-height: 75px;
              border-bottom: 1px solid #000;
            }
            .section-box:last-child {
              border-bottom: none;
            }
            .section-title {
              font-weight: bold;
              font-size: 8pt;
              text-transform: uppercase;
              margin-bottom: 4px;
              background: #f0f0f0;
              padding: 3px 6px;
              margin: -6px -8px 6px -8px;
              border-bottom: 1px solid #000;
            }
            
            /* Field rows */
            .field-row {
              margin-bottom: 3px;
              font-size: 9pt;
            }
            .field-label {
              font-weight: bold;
              font-size: 8pt;
            }
            .field-value {
              font-size: 9pt;
            }
            .field-value-bold {
              font-weight: bold;
              font-size: 10pt;
            }
            
            /* Inline fields */
            .inline-fields {
              display: flex;
              gap: 15px;
              flex-wrap: wrap;
              margin-top: 4px;
            }
            .inline-field {
              display: flex;
              gap: 4px;
              align-items: baseline;
            }
            
            /* Checkbox */
            .checkbox {
              display: inline-block;
              width: 11px;
              height: 11px;
              border: 1px solid #000;
              text-align: center;
              line-height: 9px;
              font-size: 9pt;
              margin-right: 3px;
              vertical-align: middle;
            }
            .checkbox-row {
              display: flex;
              gap: 15px;
              margin-top: 4px;
              font-size: 8pt;
            }
            .checkbox-item {
              display: flex;
              align-items: center;
            }
            
            /* Customer order table */
            .order-section {
              border-bottom: 1px solid #000;
            }
            .section-header {
              font-weight: bold;
              font-size: 9pt;
              text-transform: uppercase;
              background: #f0f0f0;
              padding: 4px 8px;
              border-bottom: 1px solid #000;
            }
            .order-table {
              width: 100%;
              border-collapse: collapse;
            }
            .order-table th {
              background: #e8e8e8;
              border: 1px solid #000;
              padding: 4px 6px;
              font-size: 8pt;
              font-weight: bold;
              text-align: center;
              text-transform: uppercase;
            }
            .order-table td {
              border: 1px solid #000;
              padding: 4px 6px;
              font-size: 9pt;
              text-align: center;
              height: 22px;
            }
            .order-table .left-align {
              text-align: left;
            }
            .order-table .totals-row {
              background: #e8e8e8;
              font-weight: bold;
            }
            .additional-info-cell {
              text-align: left !important;
              vertical-align: top;
              font-size: 8pt;
              padding: 6px !important;
            }
            
            /* Carrier information table */
            .carrier-table-section {
              border-bottom: 1px solid #000;
            }
            .carrier-table {
              width: 100%;
              border-collapse: collapse;
            }
            .carrier-table th {
              background: #e8e8e8;
              border: 1px solid #000;
              padding: 3px 4px;
              font-size: 7pt;
              font-weight: bold;
              text-align: center;
            }
            .carrier-table td {
              border: 1px solid #000;
              padding: 3px 4px;
              font-size: 8pt;
              text-align: center;
              height: 20px;
            }
            .commodity-desc {
              font-size: 6pt;
              font-style: italic;
              text-align: left !important;
              padding: 4px !important;
            }
            
            /* Footer section */
            .footer-section {
              padding: 5px 8px;
              font-size: 7pt;
              line-height: 1.3;
            }
            .legal-text {
              margin-bottom: 4px;
            }
            .legal-bold {
              font-weight: bold;
            }
            
            /* COD row */
            .cod-row {
              display: flex;
              gap: 20px;
              padding: 4px 8px;
              border-top: 1px solid #000;
              font-size: 8pt;
              align-items: center;
            }
            
            /* Signature section */
            .signature-section {
              display: flex;
              border-top: 1px solid #000;
            }
            .signature-box {
              width: 50%;
              padding: 6px 8px;
              border-right: 1px solid #000;
              min-height: 100px;
            }
            .signature-box:last-child {
              border-right: none;
            }
            .signature-title {
              font-weight: bold;
              font-size: 8pt;
              text-transform: uppercase;
              margin-bottom: 4px;
            }
            .signature-text {
              font-size: 7pt;
              margin-bottom: 6px;
              line-height: 1.3;
            }
            .signature-line {
              border-bottom: 1px solid #000;
              height: 25px;
              margin-top: 10px;
            }
            .signature-options {
              margin-top: 6px;
            }
            .signature-option-title {
              font-weight: bold;
              font-size: 7pt;
              margin-bottom: 2px;
            }
            .signature-checkboxes {
              display: flex;
              gap: 10px;
              flex-wrap: wrap;
              font-size: 7pt;
            }
            
            @media print {
              body { 
                -webkit-print-color-adjust: exact !important;
                print-color-adjust: exact !important;
              }
            }
          </style>
        </head>
        <body>
          <div class="bol-container">
            <!-- Header -->
            <div class="bol-header">
              <h1>BILL OF LADING</h1>
              <div class="header-right">
                <div class="page-number">Page 1 of 1</div>
                <div class="bol-number-row">
                  <span class="field-label">Bill of Lading Number:</span>
                  <span class="bol-number-value">${bolData.bol_number}</span>
                </div>
                <div class="barcode-space">BAR CODE SPACE</div>
              </div>
            </div>
            
            <!-- Row 1: Ship From / Carrier Info -->
            <div class="main-row">
              <!-- Left: Ship From -->
              <div class="col-left">
                <div class="section-box">
                  <div class="section-title">SHIP FROM</div>
                  <div class="field-row"><span class="field-label">Name:</span> <span class="field-value">${pickupFacility?.name || pickup?.facility_name || ''}</span></div>
                  <div class="field-row"><span class="field-label">Address:</span> <span class="field-value">${pickupFacility?.address || ''}</span></div>
                  <div class="field-row"><span class="field-label">City/State/Zip:</span> <span class="field-value">${pickupFacility?.city || ''}, ${pickupFacility?.state || ''} ${pickupFacility?.zip_code || ''}</span></div>
                  <div class="inline-fields">
                    <div class="inline-field"><span class="field-label">SID#:</span> <span class="field-value">__________</span></div>
                    <div class="inline-field"><span class="field-label">FOB:</span> <span class="checkbox"></span></div>
                  </div>
                </div>
              </div>
              
              <!-- Right: Carrier Info -->
              <div class="col-right">
                <div class="section-box">
                  <div class="field-row"><span class="field-label">CARRIER NAME:</span> <span class="field-value-bold">${bolData.carrier_name}</span></div>
                  <div class="inline-fields">
                    <div class="inline-field"><span class="field-label">Trailer number:</span> <span class="field-value">${pdfTrailerNumber || '_____'}</span></div>
                    <div class="inline-field"><span class="field-label">Seal number(s):</span> <span class="field-value">${bolData.seal_number || '_____'}</span></div>
                  </div>
                  <div class="inline-fields">
                    <div class="inline-field"><span class="field-label">SCAC:</span> <span class="field-value-bold">${bolData.scac}</span></div>
                    <div class="inline-field"><span class="field-label">Pro number:</span> <span class="field-value">${bolData.pro_number}</span></div>
                  </div>
                  <div class="barcode-space">BAR CODE SPACE</div>
                </div>
              </div>
            </div>
            
            <!-- Row 2: Ship To / Freight Terms -->
            <div class="main-row">
              <!-- Left: Ship To -->
              <div class="col-left">
                <div class="section-box">
                  <div class="section-title">SHIP TO</div>
                  <div class="field-row"><span class="field-label">Name:</span> <span class="field-value">${deliveryFacility?.name || delivery?.facility_name || ''}</span></div>
                  <div class="field-row"><span class="field-label">Address:</span> <span class="field-value">${deliveryFacility?.address || ''}</span></div>
                  <div class="field-row"><span class="field-label">City/State/Zip:</span> <span class="field-value">${deliveryFacility?.city || ''}, ${deliveryFacility?.state || ''} ${deliveryFacility?.zip_code || ''}</span></div>
                  <div class="inline-fields">
                    <div class="inline-field"><span class="field-label">SID#:</span> <span class="field-value">__________</span></div>
                    <div class="inline-field"><span class="field-label">FOB:</span> <span class="checkbox"></span></div>
                  </div>
                </div>
              </div>
              
              <!-- Right: Freight Charge Terms -->
              <div class="col-right">
                <div class="section-box">
                  <div class="section-title">Freight Charge Terms:</div>
                  <div class="checkbox-row">
                    <div class="checkbox-item"><span class="checkbox">${bolData.freight_terms === 'prepaid' ? '✓' : ''}</span> Prepaid</div>
                    <div class="checkbox-item"><span class="checkbox">${bolData.freight_terms === 'collect' ? '✓' : ''}</span> Collect</div>
                    <div class="checkbox-item"><span class="checkbox">${bolData.freight_terms === 'third_party' ? '✓' : ''}</span> 3rd Party</div>
                  </div>
                  <div style="margin-top:8px;">
                    <span class="field-label">Master Bill of Lading:</span>
                    <div style="margin-top:3px; font-size:7pt;">
                      <span class="checkbox">${bolData.master_bol ? '✓' : ''}</span> With attached underlying Bills of Lading.
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            <!-- Row 3: Third Party / Special Instructions -->
            <div class="main-row">
              <!-- Left: Third Party Freight Charges Bill To -->
              <div class="col-left">
                <div class="section-box">
                  <div class="section-title">THIRD PARTY FREIGHT CHARGES BILL TO:</div>
                  <div class="field-row"><span class="field-label">Name:</span> <span class="field-value">${bolData.third_party_name}</span></div>
                  <div class="field-row"><span class="field-label">Address:</span> <span class="field-value">${bolData.third_party_address}</span></div>
                  <div class="field-row"><span class="field-label">City/State/Zip:</span> <span class="field-value">${bolData.third_party_city_state_zip}</span></div>
                </div>
              </div>
              
              <!-- Right: Special Instructions -->
              <div class="col-right">
                <div class="section-box">
                  <div class="section-title">SPECIAL INSTRUCTIONS:</div>
                  <div class="field-row">${bolData.special_instructions || ''}</div>
                </div>
              </div>
            </div>
            
            <!-- Customer Order Information Table -->
            <div class="order-section">
              <div class="section-header">CUSTOMER ORDER INFORMATION</div>
              <table class="order-table">
                <thead>
                  <tr>
                    <th style="width:40%;">ITEM</th>
                    <th style="width:12%;">PKGS #</th>
                    <th style="width:15%;">Weight</th>
                    <th style="width:10%;">Pallet #</th>
                    <th style="width:23%;">ADDITIONAL SHIPPER INFO</th>
                  </tr>
                </thead>
                <tbody>
                  ${bolData.line_items.map((item, idx) => `
                    <tr>
                      <td class="left-align">${item.item_description || ''}</td>
                      <td>${item.num_packages || ''}</td>
                      <td>${item.weight || ''}</td>
                      <td>${item.pallet_count || ''}</td>
                      ${idx === 0 ? `<td rowspan="${bolData.line_items.length + 1}" class="additional-info-cell">${bolData.additional_shipper_info || ''}</td>` : ''}
                    </tr>
                  `).join('')}
                  <tr class="totals-row">
                    <td class="left-align">Grand Total</td>
                    <td>${totals.totalPackages || ''}</td>
                    <td>${totals.totalWeight || ''}</td>
                    <td>${totals.totalPallets || ''}</td>
                  </tr>
                </tbody>
              </table>
            </div>
            
            <!-- Carrier Information Table -->
            <div class="carrier-table-section">
              <div class="section-header">CARRIER INFORMATION</div>
              <table class="carrier-table">
                <thead>
                  <tr>
                    <th colspan="2">HANDLING UNIT</th>
                    <th colspan="2">PACKAGE</th>
                    <th rowspan="2">WEIGHT</th>
                    <th rowspan="2">H.M.<br/>(X)</th>
                    <th rowspan="2" style="width:28%;">COMMODITY DESCRIPTION<br/><span style="font-weight:normal;font-size:5pt;">Commodities requiring special or additional care or attention in handling or stowing must be so marked and packaged as to ensure safe transportation with ordinary care. See Section 2(e) of NMFC item 360</span></th>
                    <th colspan="2">LTL ONLY</th>
                  </tr>
                  <tr>
                    <th>QTY</th>
                    <th>TYPE</th>
                    <th>QTY</th>
                    <th>TYPE</th>
                    <th>NMFC #</th>
                    <th>CLASS</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>${totals.totalPallets || ''}</td>
                    <td>PLT</td>
                    <td>${totals.totalPackages || ''}</td>
                    <td>CTN</td>
                    <td>${totals.totalWeight || ''}</td>
                    <td></td>
                    <td class="commodity-desc">General Freight</td>
                    <td></td>
                    <td></td>
                  </tr>
                </tbody>
                <tfoot>
                  <tr class="totals-row">
                    <td colspan="4" class="left-align" style="padding-left:8px;">GRAND TOTAL</td>
                    <td>${totals.totalWeight || ''}</td>
                    <td colspan="4"></td>
                  </tr>
                </tfoot>
              </table>
            </div>
            
            <!-- Legal text section -->
            <div class="footer-section">
              <div class="legal-text">
                Where the rate is dependent on value, shippers are required to state specifically in writing the agreed or declared value of the property as follows:
                "The agreed or declared value of the property is specifically stated by the shipper to be not exceeding _______ per _______."
              </div>
            </div>
            
            <!-- COD Row -->
            <div class="cod-row">
              <div><span class="field-label">COD Amount: $</span>__________</div>
              <div><span class="field-label">Fee Terms:</span> <span class="checkbox"></span> Collect <span class="checkbox"></span> Prepaid</div>
              <div><span class="field-label">Customer check acceptable:</span> <span class="checkbox"></span></div>
            </div>
            
            <!-- Liability and Receipt text -->
            <div class="footer-section" style="border-top:1px solid #000;">
              <div class="legal-text legal-bold">
                NOTE: Liability Limitation for loss or damage in this shipment may be applicable. See 49 U.S.C. § 14706(c)(1)(A) and (B).
              </div>
              <div class="legal-text">
                RECEIVED, subject to individually determined rates or contracts that have been agreed upon in writing between the carrier and shipper,
                if applicable, otherwise to the rates, classifications and rules that have been established by the carrier and are available to the shipper,
                on request, and to all applicable state and federal regulations.
              </div>
              <div class="legal-text">
                The carrier shall not make delivery of this shipment without payment of freight and all other lawful charges.
              </div>
            </div>
            
            <!-- Signature Section -->
            <div class="signature-section">
              <!-- Shipper Signature -->
              <div class="signature-box">
                <div class="signature-title">SHIPPER SIGNATURE / DATE</div>
                <div class="signature-text">
                  This is to certify that the above named materials are properly classified, packaged, marked and labeled,
                  and are in proper condition for transportation according to the applicable regulations of the DOT.
                </div>
                <div class="signature-options">
                  <div class="signature-option-title">Trailer Loaded:</div>
                  <div class="signature-checkboxes">
                    <span><span class="checkbox"></span> By Shipper</span>
                    <span><span class="checkbox"></span> By Driver</span>
                  </div>
                </div>
                <div class="signature-options">
                  <div class="signature-option-title">Freight Counted:</div>
                  <div class="signature-checkboxes">
                    <span><span class="checkbox"></span> By Shipper</span>
                    <span><span class="checkbox"></span> By Driver/pallets said to contain</span>
                    <span><span class="checkbox"></span> By Driver/Pieces</span>
                  </div>
                </div>
                <div class="signature-line"></div>
              </div>
              
              <!-- Carrier Signature -->
              <div class="signature-box">
                <div class="signature-title">CARRIER SIGNATURE / PICKUP DATE</div>
                <div class="signature-text">
                  Carrier acknowledges receipt of packages and required placards. Carrier certifies emergency response information
                  was made available and/or carrier has the DOT emergency response guidebook or equivalent documentation in the vehicle.
                </div>
                <div class="signature-line"></div>
                <div style="text-align:center; font-size:6pt; margin-top:4px;">Property described above is received in good order, except as noted.</div>
              </div>
            </div>
          </div>
        </body>
        </html>
      `;

      // Open print window via Blob URL (avoids document.write XSS vector)
      const blob = new Blob([bolHtml], { type: 'text/html' });
      const blobUrl = URL.createObjectURL(blob);
      const printWindow = window.open(blobUrl, '_blank');

      setTimeout(() => {
        if (printWindow) printWindow.print();
        URL.revokeObjectURL(blobUrl);
        setGeneratingPdf(false);
      }, 500);
      
    } catch (error) {
      console.error('PDF generation error:', error);
      toast.error('Failed to generate PDF');
      setGeneratingPdf(false);
    }
  };

  const totals = calculateTotals();

  // Get facility info for display
  const pickup = load?.stops?.find(s => s.type === 'pickup');
  const delivery = load?.stops?.filter(s => s.type === 'delivery')[0];
  const pickupFacility = facilities?.find(f => f.id === pickup?.facility_id);
  const deliveryFacility = facilities?.find(f => f.id === delivery?.facility_id);

  // Always use load.trailer_number as the source of truth for display
  const displayTrailerNumber = load?.trailer_number || '';

  return (
    <div className="space-y-6">
      {/* BOL Section */}
      <div className="bg-[var(--tms-card)] border rounded-lg p-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <FileText className="w-6 h-6 text-[var(--tms-primary)]" />
            <div>
              <h3 className="text-lg font-semibold text-[var(--tms-text)]">Bill of Lading (BOL)</h3>
              <p className="text-sm text-[var(--tms-text-muted)]">
                Generate and manage BOL documents
                {hasUnsavedChanges && (
                  <span className="ml-2 text-amber-500 font-medium">• Unsaved changes</span>
                )}
              </p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button 
              variant="outline" 
              size="sm"
              onClick={autoGenerateBol}
              data-testid="auto-generate-bol"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Auto-Generate
            </Button>
            <Button 
              variant={hasUnsavedChanges ? "default" : "outline"}
              size="sm"
              onClick={saveBolData}
              disabled={saving}
              className={hasUnsavedChanges ? "bg-green-600 hover:bg-green-700" : ""}
              data-testid="save-bol-header"
            >
              {saving ? 'Saving...' : 'Save BOL'}
            </Button>
            <Button 
              size="sm"
              onClick={exportPdf}
              disabled={generatingPdf}
              data-testid="export-bol-pdf"
            >
              <Download className="w-4 h-4 mr-2" />
              {generatingPdf ? 'Generating...' : 'Export PDF'}
            </Button>
          </div>
        </div>

        {/* BOL Header Information */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className="space-y-2">
            <Label className="text-sm">Bill of Lading #</Label>
            <Input
              value={bolData.bol_number}
              onChange={(e) => updateBolField('bol_number', e.target.value)}
              placeholder="Auto-filled from Load #"
              data-testid="bol-number"
            />
          </div>
          <div className="space-y-2">
            <Label className="text-sm">Pro Number</Label>
            <Input
              value={bolData.pro_number}
              onChange={(e) => updateBolField('pro_number', e.target.value)}
              placeholder="Auto-filled from Load #"
              data-testid="bol-pro-number"
            />
          </div>
          <div className="space-y-2">
            <Label className="text-sm">SCAC</Label>
            <Input
              value={bolData.scac}
              onChange={(e) => updateBolField('scac', e.target.value)}
              placeholder="FTMG"
              data-testid="bol-scac"
            />
          </div>
          <div className="space-y-2">
            <Label className="text-sm">Carrier Name</Label>
            <Input
              value={bolData.carrier_name}
              onChange={(e) => updateBolField('carrier_name', e.target.value)}
              placeholder="Freight Tec"
              data-testid="bol-carrier-name"
            />
          </div>
        </div>

        {/* Ship From / Ship To */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
          {/* Ship From (Pickup) */}
          <div className="p-4 bg-[var(--tms-hover)] rounded-lg">
            <h4 className="font-semibold text-[var(--tms-text)] mb-3 flex items-center gap-2">
              <ArrowUp className="w-4 h-4 text-blue-500" />
              Ship From (Pickup)
            </h4>
            <div className="space-y-1 text-sm">
              <p className="font-medium text-[var(--tms-text)]">{pickupFacility?.name || pickup?.facility_name || 'Not set'}</p>
              <p className="text-[var(--tms-text-secondary)]">{pickupFacility?.address || 'Address not available'}</p>
              <p className="text-[var(--tms-text-secondary)]">{pickupFacility?.city}, {pickupFacility?.state} {pickupFacility?.zip_code}</p>
              {pickup?.appointment_datetime && (
                <p className="text-[var(--tms-text-muted)] text-xs mt-2">
                  Pickup: {new Date(pickup.appointment_datetime).toLocaleString()}
                </p>
              )}
            </div>
          </div>

          {/* Ship To (Delivery) */}
          <div className="p-4 bg-[var(--tms-hover)] rounded-lg">
            <h4 className="font-semibold text-[var(--tms-text)] mb-3 flex items-center gap-2">
              <ArrowDown className="w-4 h-4 text-green-500" />
              Ship To (Delivery)
            </h4>
            <div className="space-y-1 text-sm">
              <p className="font-medium text-[var(--tms-text)]">{deliveryFacility?.name || delivery?.facility_name || 'Not set'}</p>
              <p className="text-[var(--tms-text-secondary)]">{deliveryFacility?.address || 'Address not available'}</p>
              <p className="text-[var(--tms-text-secondary)]">{deliveryFacility?.city}, {deliveryFacility?.state} {deliveryFacility?.zip_code}</p>
              {delivery?.appointment_datetime && (
                <p className="text-[var(--tms-text-muted)] text-xs mt-2">
                  Delivery: {new Date(delivery.appointment_datetime).toLocaleString()}
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Third Party Bill To Section */}
        <div className="p-4 bg-[var(--tms-hover)] rounded-lg mb-6">
          <h4 className="font-semibold text-[var(--tms-text)] mb-3">Third Party Freight Charges Bill To</h4>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label className="text-sm">Name</Label>
              <Input
                value={bolData.third_party_name}
                onChange={(e) => updateBolField('third_party_name', e.target.value)}
                placeholder="Freight Tec Management Group"
                data-testid="bol-third-party-name"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-sm">Address</Label>
              <Input
                value={bolData.third_party_address}
                onChange={(e) => updateBolField('third_party_address', e.target.value)}
                placeholder="190 N Main Street Suite 225"
                data-testid="bol-third-party-address"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-sm">City/State/Zip</Label>
              <Input
                value={bolData.third_party_city_state_zip}
                onChange={(e) => updateBolField('third_party_city_state_zip', e.target.value)}
                placeholder="Bountiful, Utah 84010"
                data-testid="bol-third-party-csz"
              />
            </div>
          </div>
        </div>

        {/* Carrier & Freight Terms */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
          {/* Carrier Information */}
          <div className="space-y-4">
            <h4 className="font-semibold text-[var(--tms-text)]">Carrier Information</h4>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-sm">Trailer Number</Label>
                <Input
                  value={displayTrailerNumber}
                  onChange={(e) => updateBolField('trailer_number', e.target.value)}
                  placeholder="Trailer #"
                  data-testid="bol-trailer-number"
                  className="bg-[var(--tms-input-bg)]"
                  readOnly
                />
                <p className="text-xs text-[var(--tms-text-muted)]">Auto-synced from Carrier tab</p>
              </div>
              <div className="space-y-2">
                <Label className="text-sm">Seal Number</Label>
                <Input
                  value={bolData.seal_number}
                  onChange={(e) => updateBolField('seal_number', e.target.value)}
                  placeholder="Seal #"
                  data-testid="bol-seal-number"
                />
              </div>
            </div>
          </div>

          {/* Freight Charge Terms */}
          <div className="space-y-4">
            <h4 className="font-semibold text-[var(--tms-text)]">Freight Charge Terms</h4>
            <div className="flex gap-6">
              <div className="flex items-center gap-2">
                <Checkbox 
                  id="prepaid"
                  checked={bolData.freight_terms === 'prepaid'}
                  onCheckedChange={() => updateBolField('freight_terms', 'prepaid')}
                  data-testid="bol-freight-prepaid"
                />
                <Label htmlFor="prepaid" className="text-sm cursor-pointer">Prepaid</Label>
              </div>
              <div className="flex items-center gap-2">
                <Checkbox 
                  id="collect"
                  checked={bolData.freight_terms === 'collect'}
                  onCheckedChange={() => updateBolField('freight_terms', 'collect')}
                  data-testid="bol-freight-collect"
                />
                <Label htmlFor="collect" className="text-sm cursor-pointer">Collect</Label>
              </div>
              <div className="flex items-center gap-2">
                <Checkbox 
                  id="third_party"
                  checked={bolData.freight_terms === 'third_party'}
                  onCheckedChange={() => updateBolField('freight_terms', 'third_party')}
                  data-testid="bol-freight-third-party"
                />
                <Label htmlFor="third_party" className="text-sm cursor-pointer">3rd Party</Label>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Checkbox 
                id="master_bol"
                checked={bolData.master_bol}
                onCheckedChange={(checked) => updateBolField('master_bol', checked)}
                data-testid="bol-master-bol"
              />
              <Label htmlFor="master_bol" className="text-sm cursor-pointer">Master Bill of Lading with attached underlying Bills</Label>
            </div>
          </div>
        </div>

        {/* Special Instructions */}
        <div className="mb-6">
          <Label className="text-sm mb-2 block">Special Instructions</Label>
          <Textarea
            value={bolData.special_instructions}
            onChange={(e) => updateBolField('special_instructions', e.target.value)}
            placeholder="Enter any special handling instructions"
            rows={2}
            data-testid="bol-special-instructions"
          />
        </div>

        {/* Line Items Table */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-3">
            <h4 className="font-semibold text-[var(--tms-text)]">Customer Order Information</h4>
            <div className="flex items-center gap-2">
              <Label className="text-sm mr-2">Weight Unit:</Label>
              <Select
                value={bolData.weight_unit}
                onValueChange={(value) => updateBolField('weight_unit', value)}
              >
                <SelectTrigger className="w-24" data-testid="bol-weight-unit">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="LBS">LBS</SelectItem>
                  <SelectItem value="KGS">KGS</SelectItem>
                </SelectContent>
              </Select>
              <Button 
                variant="outline" 
                size="sm"
                onClick={addLineItem}
                data-testid="add-bol-line"
              >
                <Plus className="w-4 h-4 mr-1" />
                Add Row
              </Button>
            </div>
          </div>

          <div className="overflow-x-auto border rounded-lg">
            <table className="data-table w-full">
              <thead>
                <tr className="bg-[var(--tms-hover)]">
                  <th className="text-left p-3 text-sm">Item Description</th>
                  <th className="text-left p-3 text-sm"># Packages</th>
                  <th className="text-left p-3 text-sm">Weight ({bolData.weight_unit})</th>
                  <th className="text-left p-3 text-sm">Pallet #</th>
                  <th className="text-center p-3 text-sm w-16">Action</th>
                </tr>
              </thead>
              <tbody>
                {bolData.line_items.map((item, index) => (
                  <tr key={index} className="border-t border-[var(--tms-border)]">
                    <td className="p-2">
                      <Input
                        value={item.item_description}
                        onChange={(e) => updateLineItem(index, 'item_description', e.target.value)}
                        placeholder="Item description"
                        className="text-sm"
                        data-testid={`bol-line-item-${index}`}
                      />
                    </td>
                    <td className="p-2">
                      <Input
                        type="number"
                        value={item.num_packages}
                        onChange={(e) => updateLineItem(index, 'num_packages', e.target.value)}
                        placeholder="0"
                        className="text-sm"
                        data-testid={`bol-line-packages-${index}`}
                      />
                    </td>
                    <td className="p-2">
                      <Input
                        type="number"
                        value={item.weight}
                        onChange={(e) => updateLineItem(index, 'weight', e.target.value)}
                        placeholder="0"
                        className="text-sm"
                        data-testid={`bol-line-weight-${index}`}
                      />
                    </td>
                    <td className="p-2">
                      <Input
                        type="number"
                        value={item.pallet_count}
                        onChange={(e) => updateLineItem(index, 'pallet_count', e.target.value)}
                        placeholder="0"
                        className="text-sm"
                        data-testid={`bol-line-pallets-${index}`}
                      />
                    </td>
                    <td className="p-2 text-center">
                      {bolData.line_items.length > 1 && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => removeLineItem(index)}
                          className="text-red-500 hover:text-red-700 hover:bg-red-100"
                          data-testid={`bol-line-remove-${index}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="bg-[var(--tms-hover)] font-semibold border-t-2 border-[var(--tms-border)]">
                  <td className="p-3 text-sm">GRAND TOTAL</td>
                  <td className="p-3 text-sm" data-testid="bol-total-packages">{totals.totalPackages}</td>
                  <td className="p-3 text-sm" data-testid="bol-total-weight">{totals.totalWeight} {bolData.weight_unit}</td>
                  <td className="p-3 text-sm" data-testid="bol-total-pallets">{totals.totalPallets}</td>
                  <td className="p-3"></td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>

        {/* Additional Shipper Information */}
        <div className="mb-6">
          <Label className="text-sm mb-2 block">Additional Shipper Information</Label>
          <Textarea
            value={bolData.additional_shipper_info}
            onChange={(e) => updateBolField('additional_shipper_info', e.target.value)}
            placeholder="Enter packing list references, special notes, etc."
            rows={3}
            className="w-full"
            data-testid="bol-additional-info"
          />
        </div>

        {/* Save BOL Button - Bottom */}
        <div className="flex justify-end gap-3 pt-4 border-t border-[var(--tms-border)]">
          {hasUnsavedChanges && (
            <span className="text-amber-500 text-sm self-center mr-2">You have unsaved changes</span>
          )}
          <Button
            variant={hasUnsavedChanges ? "default" : "outline"}
            onClick={saveBolData}
            disabled={saving}
            className={hasUnsavedChanges ? "bg-green-600 hover:bg-green-700" : ""}
            data-testid="save-bol"
          >
            {saving ? 'Saving...' : 'Save BOL Data'}
          </Button>
        </div>
      </div>

      {/* BOL Version History */}
      {bolVersions.length > 0 && (
        <div className="bg-[var(--tms-card)] border rounded-lg p-6">
          <div className="flex items-center gap-2 mb-4">
            <History className="w-5 h-5 text-[var(--tms-text-muted)]" />
            <h3 className="font-semibold text-[var(--tms-text)]">Version History</h3>
          </div>
          <div className="space-y-2">
            {bolVersions.map((version, index) => (
              <div 
                key={version.id || version.created_at || index}
                className="flex items-center justify-between p-3 bg-[var(--tms-hover)] rounded-lg text-sm"
              >
                <div>
                  <span className="font-medium text-[var(--tms-text)]">Version {bolVersions.length - index}</span>
                  <span className="text-[var(--tms-text-muted)] ml-3">
                    {new Date(version.created_at).toLocaleString()}
                  </span>
                </div>
                <Button variant="ghost" size="sm" onClick={() => restoreVersion(version)}>
                  Restore
                </Button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default BOLGenerator;
